import React from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Users, Award, Truck, Heart, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function AboutPage() {
  const values = [
    { icon: Leaf, title: 'Pure & Organic', desc: 'All our products are 100% organic, sourced from certified farms' },
    { icon: Users, title: 'Farmer First', desc: 'We work directly with farmers, ensuring fair prices and sustainable practices' },
    { icon: Shield, title: 'Quality Assured', desc: 'Every product goes through strict quality checks before reaching you' },
    { icon: Heart, title: 'Health Focused', desc: 'We believe in providing food that nourishes the body and soul' }
  ];

  const stats = [
    { number: '10+', label: 'Years of Trust' },
    { number: '15,000+', label: 'Happy Families' },
    { number: '4,000+', label: 'Farmer Partners' },
    { number: '100+', label: 'Organic Products' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="bg-primary text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-serif text-4xl lg:text-5xl font-semibold leading-tight">
                Bringing Purity to Your Plate
              </h1>
              <p className="mt-6 text-lg text-white/80 leading-relaxed">
                SriSuktam Organics was born from a simple belief - that everyone deserves access
                to pure, chemical-free food. We partner with local farmers across India to bring
                you the finest organic products, processed using traditional methods.
              </p>
              <p className="mt-4 text-lg text-white/80 leading-relaxed italic">
                "Swasthya Shuddi" - Purity for Health
              </p>
            </div>
            <div className="relative">
              <img
                src="/logosrisuktam.jpg"
                alt="SriSuktam Organics"
                className="w-64 h-64 mx-auto bg-white/10 rounded-3xl p-6"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl lg:text-5xl font-serif font-semibold text-primary">
                  {stat.number}
                </p>
                <p className="mt-2 text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="/our_story.png"
                alt="Farmer in field"
                className="rounded-2xl shadow-lg"
              />
            </div>
            <div>
              <span className="text-sm font-medium text-primary uppercase tracking-wider">Our Story</span>
              <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-stone-900 mt-4">
                From Farm to Your Family
              </h2>
              <p className="mt-6 text-stone-600 leading-relaxed">
                Our journey began over a decade ago when we witnessed the disconnect between
                consumers seeking healthy food and farmers practicing traditional organic agriculture.
                We decided to bridge this gap.
              </p>
              <p className="mt-4 text-stone-600 leading-relaxed">
                Today, we work with over 4,000 farmers across Karnataka, Kerala, Tamil Nadu, and
                other states, bringing you products like cold-pressed oils made in traditional
                wooden kolhu, A2 ghee from indigenous cow breeds, and millets grown using
                age-old farming practices.
              </p>
              <p className="mt-4 text-stone-600 leading-relaxed">
                Every product we sell carries the trust of our farmers and the promise of purity
                to your family.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-primary uppercase tracking-wider">Our Values</span>
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-stone-900 mt-4">
              What We Stand For
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 border border-stone-100 text-center">
                <div className="h-14 w-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-semibold text-stone-900 mb-2">{value.title}</h3>
                <p className="text-sm text-muted-foreground">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-white">
            Ready to Experience Pure Organic?
          </h2>
          <p className="mt-4 text-white/80 text-lg">
            Join thousands of families who trust SriSuktam Organics for their daily nutrition
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/products">
              <Button className="bg-white text-secondary hover:bg-white/90 rounded-full px-8 py-6 text-lg">
                Shop Now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
